import type { CreateModelState, CreateModelAction } from './types'

export const initialState: CreateModelState = {
  mode: 'train',
  modelName: '',
  modelType: 'style',
  triggerWord: '',
  images: [],
  isDragging: false,
  captioningStatus: 'idle',
  captioningProgress: 0,
  expandedImageId: null,
  selectedTrainerId: null,
  steps: 1000,
  learningRate: 0.0001,
  advancedSettings: {},
  importUrl: '',
  importPreviewUrl: null,
  submitStatus: 'idle',
  submitError: null,
  submitErrorTitle: null,
  uploadProgress: 0,
  preprocessingOp: null,
  preprocessingProgress: 0,
}

export function createModelReducer(
  state: CreateModelState,
  action: CreateModelAction,
): CreateModelState {
  switch (action.type) {
    case 'SET_MODE':
      return { ...state, mode: action.payload }

    case 'SET_MODEL_NAME':
      return { ...state, modelName: action.payload }

    case 'SET_MODEL_TYPE':
      return { ...state, modelType: action.payload }

    case 'SET_TRIGGER_WORD':
      return { ...state, triggerWord: action.payload }

    case 'ADD_IMAGES': {
      const combined = [...state.images, ...action.payload]
      return { ...state, images: combined }
    }

    case 'REMOVE_IMAGE': {
      const img = state.images.find((i) => i.id === action.payload)
      if (img) URL.revokeObjectURL(img.objectUrl)
      return {
        ...state,
        images: state.images.filter((i) => i.id !== action.payload),
        expandedImageId: state.expandedImageId === action.payload ? null : state.expandedImageId,
      }
    }

    case 'DISMISS_ISSUE':
      return {
        ...state,
        images: state.images.map((img) =>
          img.id === action.payload.imageId
            ? { ...img, issues: img.issues.filter((i) => i.type !== action.payload.issueType) }
            : img,
        ),
      }

    case 'DISMISS_ISSUE_BULK':
      return {
        ...state,
        images: state.images.map((img) => ({
          ...img,
          issues: img.issues.filter((i) => i.type !== action.payload),
        })),
      }

    case 'UPDATE_CAPTION':
      return {
        ...state,
        images: state.images.map((img) =>
          img.id === action.payload.imageId
            ? { ...img, caption: action.payload.caption, captionStatus: action.payload.status }
            : img,
        ),
      }

    case 'SET_DRAGGING':
      return { ...state, isDragging: action.payload }

    case 'START_CAPTIONING':
      return { ...state, captioningStatus: 'running', captioningProgress: 0 }

    case 'CAPTION_IMAGE':
      return {
        ...state,
        captioningProgress: state.captioningProgress + 1,
        images: state.images.map((img) =>
          img.id === action.payload.imageId
            ? { ...img, caption: action.payload.caption, captionStatus: 'auto' }
            : img,
        ),
      }

    case 'FINISH_CAPTIONING':
      return { ...state, captioningStatus: 'done' }

    case 'SET_EXPANDED_IMAGE':
      return { ...state, expandedImageId: action.payload }

    case 'SELECT_TRAINER':
      return {
        ...state,
        selectedTrainerId: action.payload.trainerId,
        steps: action.payload.defaults.steps,
        learningRate: action.payload.defaults.learningRate,
        advancedSettings: action.payload.defaults.advanced,
      }

    case 'SET_STEPS':
      return { ...state, steps: action.payload }

    case 'SET_LEARNING_RATE':
      return { ...state, learningRate: action.payload }

    case 'SET_ADVANCED':
      return {
        ...state,
        advancedSettings: { ...state.advancedSettings, [action.payload.key]: action.payload.value },
      }

    case 'SET_IMPORT_URL':
      return { ...state, importUrl: action.payload }

    case 'SET_IMPORT_PREVIEW':
      return { ...state, importPreviewUrl: action.payload }

    case 'SET_SUBMIT_STATUS':
      return {
        ...state,
        submitStatus: action.payload.status,
        submitError: action.payload.error ?? null,
        submitErrorTitle: action.payload.errorTitle ?? null,
        uploadProgress: action.payload.status === 'uploading' ? state.uploadProgress : 0,
      }

    case 'SET_UPLOAD_PROGRESS':
      return { ...state, uploadProgress: action.payload }

    case 'START_PREPROCESSING':
      return { ...state, preprocessingOp: action.payload, preprocessingProgress: 0 }

    case 'PREPROCESSING_PROGRESS':
      return { ...state, preprocessingProgress: action.payload }

    case 'FINISH_PREPROCESSING':
      return {
        ...state,
        preprocessingOp: null,
        preprocessingProgress: 0,
        ...(action.payload ? { images: action.payload.images } : {}),
      }

    case 'REMOVE_IMAGES_BULK': {
      const idsToRemove = new Set(action.payload)
      for (const img of state.images) {
        if (idsToRemove.has(img.id)) URL.revokeObjectURL(img.objectUrl)
      }
      return {
        ...state,
        images: state.images.filter((i) => !idsToRemove.has(i.id)),
        expandedImageId: state.expandedImageId && idsToRemove.has(state.expandedImageId) ? null : state.expandedImageId,
      }
    }

    case 'UPDATE_IMAGE':
      return {
        ...state,
        images: state.images.map((img) =>
          img.id === action.payload.imageId ? { ...img, ...action.payload.updates } : img,
        ),
      }

    case 'RESET': {
      // Revoke all object URLs before resetting
      for (const img of state.images) {
        URL.revokeObjectURL(img.objectUrl)
      }
      return { ...initialState }
    }

    default:
      return state
  }
}
